import React from "react";
import styled from "styled-components";
import Main from "./components/Main";
import Header from "./components/Header";
import GlobalModal from "./components/GlobalModal";
import { getRouteByPath } from "./routes";
import { withRouter } from "react-router-dom";
import { StyleProvider, withNavigation } from "./services/visualLibraryService";
import EventsApi from "./helpers/key-handler";
import { withAppState, getAppState } from "./state";
import getMenuItems from "./components/MainMenuContainer/menuItems";
import { config } from "./config/config";
import { i18n } from "./services/i18nService";
import { getSdk } from "./services/sdkService";
import { executeDeepLinkAction } from "./services/deepLinkService";
import { getLogger, LoggerOverlay } from "./utils/logger";
import { getThemeObject } from "./utils/getThemeObject";
import GlobalStyles from "./styles/global-styles";
import "./styles/fonts.css";
import * as Sentry from "@sentry/react";
import ReactGA from "react-ga4";

const historyBackFocusStack = [];
let localHistory;
let appStarted = false;
let currentComponent = null;
let previousPath = [];
let historyBackFocus = null;
let dismissModal;
let currentRouteFocus = null;
let previousRouteFocus = null;
let previousFocus = null;
let isPlaybackAnalyticsInitializated = false;

// GA4 ID
const ga4Id = process.env.NODE_ENV === 'production' ? "G-FQF30LM34H" : "G-PSL99VZEE9";
// initializing ga4
ReactGA.initialize(ga4Id);

let Logger = getLogger(undefined, { enabled: false });

/**
 * Inits the debugger overlay component.
 * This should depend on the current build config.
 * @param loggerOverLay
 */
const initOverlayLogger = loggerOverLay => {
  Logger = getLogger(undefined, { enabled: true, appender: loggerOverLay });
};

/**
 * Returns whether the passed router path must have the main menu.
 * @param url The current URL location.
 * @return {boolean} The location must have the menu.
 */
const hasHeader = url =>
  url.search(
    /\/(series|movies|.+\/series|.+\/movies)\/\d|\/(player|login|pin|landing|signup|redeem|thankyou|search|explore|mylibrary|splash|settings|parentalControl)/
  ) !== 0;

/**
 * Navigates back in the router history.
 * @param history The router history.
 */
const goBack = history => history.goBack();

/**
 * Finds the current page component default focus depending on layout direction.
 * @param component
 * @return {*}
 */
const getDefaultFocus = (component = currentComponent) =>
  i18n.getDirection() === i18n.directions.RIGHT_TO_LEFT
    ? component && (component.defaultFocusPathRTL || component.defaultFocusPath)
    : component && (component.defaultFocusPathLTR || component.defaultFocusPath);

/**
 * Sets the focus to the previous or default focus element in that order.
 * @return {boolean} Whether the focus could be set.
 */
const focusPreviousDefault = () => {
  if (currentComponent && currentComponent.props.setFocus) {
    const defaultFocus = previousFocus || getDefaultFocus();
    currentComponent.props.setFocus(defaultFocus);
    return !!defaultFocus;
  }
  return false;
};


/**
 * Bind back button.
 * First, the onBack method of the current page will be called if present.
 * If the method returns false or is not present, goBack in location will be called.
 */
/* 
Here new checks for forcelogout added beacuse not allow  Back action for force logout popup 
*/
EventsApi({
  BACK: () => {
    return (
    (!getAppState().forceLogout && dismissModal && dismissModal(focusPreviousDefault)) ||
    (currentComponent && currentComponent.onBack && !getAppState().forceLogout && currentComponent.onBack()) ||
    !getAppState().forceLogout && goBack(localHistory)
    );
  }
});

/**
 * Keeps track of the current page component for future actions.
 * @param component
 * @return {*}
 */
const onRef = component => (currentComponent = component);

const GlobalThemeContainer = styled.div`
  width: 100%;
  height: 100%;
  position: fixed;
  background: ${props => props.theme.background};
  /* Reset background if player visible */
  .isPlayerVisible & {
    background: transparent;
  }
`;

let historyObject = null;
const handleDeepLinking = deepLinkData => executeDeepLinkAction.call({ history: historyObject }, deepLinkData);

/**
 * Global Focus HOC.
 * In case a focus must be ignored for back navigation (i.e.: modal popups)
 * the focusPath props must contain the 'passThru' string
 */
const NavigationWrapper = withNavigation(
  ({
    currentFocusPath,
    hasHeader,
    initialFocus,
    setFocus,
    pauseNavigation,
    resumeNavigation,
    applicationState: { userLocale, globalModal, theme },
    stateActions: { setCurrentFocus }
  }) => {
    const direction = config.get().locales[userLocale] && config.get().locales[userLocale].direction;

    if (currentRouteFocus !== currentFocusPath) {
      setCurrentFocus(currentFocusPath);
    }

    if (globalModal && globalModal.dismiss) {
      dismissModal = globalModal.dismiss;
    } else {
      dismissModal = null;
    }

    currentRouteFocus &&
      typeof currentRouteFocus == "string" &&
      currentRouteFocus.indexOf("passThru") === -1 &&
      (previousFocus = currentRouteFocus);
    currentRouteFocus = currentFocusPath;

    return (
      <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
        <StyleProvider theme={getThemeObject(direction, theme)}>
          <GlobalStyles />
          {globalModal && <GlobalModal modalConfig={globalModal} />}
          <GlobalThemeContainer>
            {hasHeader && <Header />}
            <Main
              initialFocus={initialFocus}
              onRef={onRef}
              setFocus={setFocus}
              logger={Logger}
              pauseNavigation={pauseNavigation}
              resumeNavigation={resumeNavigation}
            />
          </GlobalThemeContainer>
        </StyleProvider>
      </Sentry.ErrorBoundary>
    );
  }
);

/**
 * Checks whether the current navigation fits the conditions to have an initialFocus.
 * It checks if is Home and is not the first load or is any other root level page.
 * @param page The page router to load.
 * @param applicationState The application state object.
 * @return {boolean} The assertion is right.
 */
const isFilteredFromInitialFocus = (page, applicationState) =>
  appStarted && page.isRoot && !applicationState.userLogged;

/**
 * Returns the initial default focused element for the current route.
 * @param location The current router location.
 * @param applicationState The application state object.
 * @return {boolean|*|string} The default focus if available.
 */
const getInitialFocus = (location, applicationState) => {
  if (applicationState.globalModal) {
    return undefined;
  }
  const toFocus = getRouteByPath(location.pathname);
  return toFocus && !isFilteredFromInitialFocus(toFocus, applicationState) && getDefaultFocus(toFocus.component);
};

/**
 * Sets referrer to history.location
 * @param {Object} history
 */
const setReferrer = history => {
  if (!previousPath.includes(history.location.pathname)) {
    previousPath.push(history.location.pathname);
    history.location.referrer = previousPath[0];
    previousPath.length > 1 && previousPath.shift();
  }
};

const getDefaultMenuFocusedItem = location => {
  let activeMainMenuItemIndex = 0;
  if (appStarted) {
    activeMainMenuItemIndex = getMenuItems().findIndex(item => {
      return location.pathname === item.path;
    });
  }
  return Math.max(activeMainMenuItemIndex, 0);
};

/**
 * Adds the last focus to the current page focus history depending on string flags ("passThru").
 */
const addToFocusHistory = () => {
  const currentRoute = (currentRouteFocus && currentRouteFocus.indexOf("passThru")) || -1;
  if (currentRoute !== -1) {
    historyBackFocusStack.push(previousFocus);
  } else {
    historyBackFocusStack.push(currentRouteFocus);
  }
};

const App = withRouter(
  withAppState(({ location, history, applicationState, stateActions }) => {
    const debug = config.get().debug;
    const isHeaderShown = hasHeader(location.pathname);
    const initialFocus = getInitialFocus(location, applicationState);
    historyObject = history;
    if (location.pathname !== previousPath[0]) {
      if (history.action === "POP") {
        historyBackFocus = historyBackFocusStack.pop() || previousRouteFocus;
        const selectedMainMenuItem = getMenuItems()[applicationState.focusedMainMenuItemIndex];
        if (selectedMainMenuItem && !selectedMainMenuItem.sticky) {
          applicationState.focusedMainMenuItemIndex = getDefaultMenuFocusedItem(location);
        }
      } else if (history.action === "REPLACE") {
        historyBackFocus = null;
        if (previousPath[0] !== "/") {
          // Only if app is not starting from the base route, i.e.: first launch.
          applicationState.focusedMainMenuItemIndex = getDefaultMenuFocusedItem(location);
        }
        if (location.state && location.state.preserveFocus) {
          historyBackFocus = currentRouteFocus;
        }
      } else if (history.action === "PUSH") {
        addToFocusHistory();
        historyBackFocus = null;
        applicationState.focusedMainMenuItemIndex = getDefaultMenuFocusedItem(location);
      }
      previousRouteFocus = currentRouteFocus;
      currentComponent = null;
    }
    setReferrer(history);
    localHistory = history;
    applicationState.appStarted = !appStarted ? false : getAppState().appStarted;
    appStarted = true;

    async function initPlaybackAnalytics(player) {
      isPlaybackAnalyticsInitializated = true;
      const sdk = await getSdk();
      console.log("Initalize PLayback Analytics");
      applicationState.player = null;
      sdk.playerManager.initPlaybackAnalytics(player);
    }
    !!config.get().featureToggles.playbackAnalytics &&
      applicationState.player &&
      initPlaybackAnalytics(applicationState.player);
    return (
      <>
        {(debug.enabled || window.__debugEnabled) && <LoggerOverlay onRef={ref => initOverlayLogger(ref)} />}
        <NavigationWrapper
          hasHeader={isHeaderShown}
          applicationState={applicationState}
          stateActions={stateActions}
          initialFocus={historyBackFocus || initialFocus || ""}
        />
      </>
    );
  })
);

/**
 * Function that deals with setting up the listeners that need to be created during application bootstrapping.
 */
async function setupListeners() {
  const sdk = await getSdk();
  sdk.systemManager.application.getDeepLinkTitle && sdk.systemManager.application.on("deepLink", handleDeepLinking);
  sdk.systemManager.network.on("networkDisconnected", () => {
    Logger.warn("DISCONNECTED", "Network");
    getAppState().isOnline = false;
  });

  sdk.systemManager.network.on("networkConnected", () => {
    Logger.warn("CONNECTED", "Network");
    getAppState().isOnline = true;
  });
}

setupListeners();

export default App;
